import { useQueryClient } from '@tanstack/react-query';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import {
  bulkCreateSuppressions,
  createSuppression,
  deleteSuppression,
  getSuppressions,
} from '../services/suppressions.service';
import type {
  BulkSuppressionDataApi,
  CreateSuppressionRequestApi,
  SuppressionChannelApi,
  SuppressionEntryApi,
  SuppressionListDataApi,
  SuppressionReasonApi,
} from '../types/suppressions-api.types';

export type UseSuppressionsListParams = {
  pageNo?: number;
  pageSize?: number;
  q?: string;
  reason?: SuppressionReasonApi;
  channel?: SuppressionChannelApi;
};

const invalidateSuppressionQueries = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: ['suppressions'] });
};

const invalidateSuppressionAndContactQueries = (
  queryClient: ReturnType<typeof useQueryClient>
) => {
  invalidateSuppressionQueries(queryClient);
  queryClient.invalidateQueries({ queryKey: ['contacts'] });
};

const toListQueryParams = ({
  pageNo = 1,
  pageSize = 50,
  q = '',
  reason,
  channel,
}: UseSuppressionsListParams) => {
  const trimmedSearch = q.trim();

  return {
    limit: pageSize,
    offset: Math.max(0, (pageNo - 1) * pageSize),
    sort: 'created' as const,
    order: 'desc' as const,
    ...(trimmedSearch ? { q: trimmedSearch } : {}),
    ...(reason ? { reason } : {}),
    ...(channel ? { channel } : {}),
  };
};

export const useSuppressionsList = (
  params: UseSuppressionsListParams = {},
  options: { enabled?: boolean } = {}
) => {
  const pageNo = params.pageNo ?? 1;
  const pageSize = params.pageSize ?? 50;
  const trimmedSearch = params.q?.trim() ?? '';

  return useGlobalQuery<SuppressionListDataApi>({
    queryKey: ['suppressions', { pageNo, pageSize, q: trimmedSearch, reason: params.reason, channel: params.channel }],
    queryFn: () => getSuppressions(toListQueryParams({ ...params, pageNo, pageSize })),
    staleTime: 0,
    refetchOnMount: 'always',
    enabled: options.enabled ?? true,
  });
};

export const useCreateSuppression = () => {
  const queryClient = useQueryClient();

  return useGlobalMutation<
    SuppressionEntryApi,
    Error,
    { address: string; channel: SuppressionChannelApi; idempotencyKey: string }
  >({
    mutationFn: createSuppression,
    suppressToast: true,
    onSuccess: () => {
      invalidateSuppressionQueries(queryClient);
    },
  });
};

export const useBulkCreateSuppressions = () => {
  const queryClient = useQueryClient();

  return useGlobalMutation<
    BulkSuppressionDataApi,
    Error,
    { entries: CreateSuppressionRequestApi[]; idempotencyKey: string }
  >({
    mutationFn: bulkCreateSuppressions,
    suppressToast: true,
    onSuccess: () => {
      invalidateSuppressionAndContactQueries(queryClient);
    },
  });
};

export const useDeleteSuppression = () => {
  const queryClient = useQueryClient();

  return useGlobalMutation<void, Error, { itemId: string; idempotencyKey: string }>({
    mutationFn: deleteSuppression,
    suppressToast: true,
    onSuccess: () => {
      invalidateSuppressionQueries(queryClient);
    },
  });
};
